// src/modules/Profile.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
  Avatar,
  Chip,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';

const ProfilePage = () => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const notify = useNotify();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = JSON.parse(localStorage.getItem('token'));
        
        const response = await fetch(`${process.env.REACT_APP_URL}/admin/admins/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Session expired. Please login again.');
          }
          throw new Error('Failed to fetch profile');
        }

        const data = await response.json();
        setUserDetails(data);
      } catch (err) {
        console.error('Error:', err);
        setError(err.message);
        notify(err.message, { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [notify]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'supervisor':
        return 'primary';
      case 'manager':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1400px', mx: 'auto', width: '100%' }}>
      {/* Header Card - updated colors to match admin theme */}
      <Card sx={{ mb: 3, overflow: 'hidden', borderRadius: 2 }}>
        <Box sx={{ 
          height: 130, 
          background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          position: 'relative',
        }} />
        
        <CardContent sx={{ position: 'relative', pt: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: { xs: 'center', sm: 'flex-start' },
            mb: 2,
            mt: -6
          }}>
            <Avatar 
              sx={{ 
                width: { xs: 80, sm: 100 }, 
                height: { xs: 80, sm: 100 }, 
                bgcolor: theme => theme.palette.primary.main, 
                border: '4px solid white',
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
              }}
            >
              <Typography variant="h3" component="span">
                {(userDetails.name?.[0] || userDetails.username?.[0] || '?').toUpperCase()}
              </Typography>
            </Avatar>
          </Box>
          
          <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              {userDetails.name || userDetails.username}
            </Typography>
            <Chip 
              label={userDetails.role || 'User'} 
              color={getRoleColor(userDetails.role)} 
              size="medium"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Profile Information Card - full width */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: '#1976d2' }} /> Profile Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <PersonIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: '#1976d2' }} /> Full Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {userDetails.name || 'Not provided'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <BadgeIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: '#1976d2' }} /> Username
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {userDetails.username}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <PhoneIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: '#1976d2' }} /> Phone Number
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {userDetails.phonenumber || 'Not provided'}
              </Typography>
            </Grid>

            {userDetails.email && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <EmailIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: '#1976d2' }} /> Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {userDetails.email}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <BadgeIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: '#1976d2' }} /> Role
              </Typography>
              <Chip 
                label={userDetails.role || 'User'} 
                color={getRoleColor(userDetails.role)} 
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <CalendarIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: '#1976d2' }} /> Member Since
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDate(userDetails.created_at)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfilePage;